import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AssortiTable from './AssortiTable';
import { fetchApi, uploadFile } from '../config/api';
import { API_URL } from '../config/constants';
import Modal from './Modal';
import VendorSelector from './VendorSelector';
import ProductSelector, { Product } from './ProductSelector';

interface Vendor {
  _id: string;
  producerId: string;
  name: string;
  companyName: string;
  email: string;
  phone: string;
  address: string;
  note?: string;
}

interface OrderFormData {
  orderNumber: string;
  orderDate: string;
  terminDate: string;
  paymentType: string;
  manufacturerCode: string;
  manufacturerCompany: string;
  modelName: string;
  merchantSubGroup: string;
  buyer: string;
  season: string;
  productDescription: string;
  yiInspectionDate: string;
  brand: string;
  deliveryLocation: string;
  totalOrderQuantity: number;
  totalAmount: number;
  kdvRate: number;
  kdvTotal: number;
  optionPrice: string;
  productImage: File | null;
  status: string;
  optimumValue: string;
  orderCount: string;
  assortiColumns: { name: string; order: number }[];
  assortiValues: { columnName: string; value: number }[];
  vendorId?: string;
  selectedProduct?: Product;
}

const CreateOrder: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [lastOrderNumber, setLastOrderNumber] = useState<string>('0000000000');

  // Satıcıları getir
  const fetchVendors = async () => {
    try {
      const response = await fetch(`${API_URL}/vendors`);
      if (!response.ok) throw new Error('Satıcılar yüklenemedi');
      const data = await response.json();
      setVendors(data);
    } catch (error) {
      console.error('Satıcılar yüklenirken hata oluştu:', error);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // Satıcı seçildiğinde form verilerini güncelle
  const handleVendorSelect = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setFormData(prev => ({
      ...prev,
      vendorId: vendor._id,
      manufacturerCompany: vendor.companyName,
      manufacturerCode: vendor.producerId
    }));
  };

  // Sipariş numarası oluştur - yeni algoritma
  const generateOrderNumber = async () => {
    try {
      // En son siparişi getir
      const response = await fetch(`${API_URL}/orders?sort=-orderNumber&limit=1`);
      
      if (!response.ok) {
        throw new Error('Sipariş numarası oluşturulurken bir hata oluştu');
      }
      
      const data = await response.json();
      
      // Eğer sipariş varsa, numarayı al ve 1 arttır, yoksa başlangıç değerini kullan
      let nextOrderNumber: number;
      
      if (data && data.length > 0 && data[0].orderNumber) {
        // Son sipariş numarasını alıp sayıya çevir ve 1 ekle
        nextOrderNumber = parseInt(data[0].orderNumber, 10) + 1;
      } else {
        // Hiç sipariş yoksa başlangıç değeri olarak 1 kullan
        nextOrderNumber = 1;
      }
      
      // 10 haneli olacak şekilde sıfırlarla doldur
      const paddedOrderNumber = nextOrderNumber.toString().padStart(10, '0');
      
      setLastOrderNumber(paddedOrderNumber);
      return paddedOrderNumber;
    } catch (error) {
      console.error('Sipariş numarası oluşturma hatası:', error);
      // Hata durumunda varsayılan bir değer kullan
      return '0000000001';
    }
  };

  // Bileşen yüklendiğinde sipariş numarası oluştur
  useEffect(() => {
    const initializeOrderNumber = async () => {
      const orderNumber = await generateOrderNumber();
      setFormData(prev => ({
        ...prev,
        orderNumber
      }));
    };
    
    initializeOrderNumber();
  }, []);

  // Şu anki tarih ve saati formatla
  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm" formatında
  };

  const [formData, setFormData] = useState<OrderFormData>({
    orderNumber: generateOrderNumber(),
    orderDate: getCurrentDateTime(),
    terminDate: getCurrentDateTime(),
    paymentType: 'Peşin',
    manufacturerCode: '',
    manufacturerCompany: '',
    modelName: '',
    merchantSubGroup: '',
    buyer: 'Baleca',
    season: '',
    productDescription: '',
    yiInspectionDate: '',
    brand: 'BALECA',
    deliveryLocation: 'Baleca Depoları',
    totalOrderQuantity: 0,
    totalAmount: 0,
    kdvRate: 10,
    kdvTotal: 0,
    optionPrice: '',
    productImage: null,
    status: 'Beklemede',
    optimumValue: '',
    orderCount: '',
    assortiColumns: [],
    assortiValues: [],
    vendorId: undefined,
    selectedProduct: undefined
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Toplam miktarı hesapla
  useEffect(() => {
    const total = formData.assortiValues.reduce((sum, item) => sum + item.value, 0);
    setFormData(prev => ({
      ...prev,
      totalOrderQuantity: total
    }));
  }, [formData.assortiValues]);

  // KDV tutarını hesapla
  useEffect(() => {
    const kdvTotal = (formData.totalAmount * formData.kdvRate) / 100;
    setFormData(prev => ({
      ...prev,
      kdvTotal
    }));
  }, [formData.totalAmount, formData.kdvRate]);

  // Optimum değer ve sipariş adeti değiştiğinde toplam tutarı hesapla
  useEffect(() => {
    const optimumValue = parseFloat(formData.optimumValue) || 0;
    const orderCount = parseInt(formData.orderCount) || 0;
    const calculatedTotal = optimumValue * orderCount;
    const calculatedKdv = (calculatedTotal * formData.kdvRate) / 100;

    setFormData(prev => ({
      ...prev,
      totalAmount: calculatedTotal,
      kdvTotal: calculatedKdv
    }));
  }, [formData.optimumValue, formData.orderCount, formData.kdvRate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'totalAmount' || name === 'kdvRate' ? parseFloat(value) || 0 : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        productImage: e.target.files![0]
      }));
    }
  };

  const handleAssortiColumnsChange = (newColumns: { name: string; order: number }[]) => {
    setFormData(prev => ({
      ...prev,
      assortiColumns: newColumns
    }));
  };

  const handleAssortiValuesChange = (newValues: { columnName: string; value: number }[]) => {
    setFormData(prev => ({
      ...prev,
      assortiValues: newValues
    }));
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setFormData(prev => ({
      ...prev,
      selectedProduct: product
    }));
  };

  const showModal = (title: string, message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setModal({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      // Ürün görseli varsa ekle
      if (formData.productImage) {
        formDataToSend.append('productImage', formData.productImage);
      }

      // Sipariş verilerini hazırla
      const orderData = {
        ...formData,
        imageUrl: formData.productImage ? `${API_URL}/assets/images/${formData.productImage.name}` : null,
      };

      // orderData'yı JSON olarak ekle
      formDataToSend.append('orderData', JSON.stringify(orderData));
      
      // API'ye gönder
      const response = await fetch(`${API_URL}/orders`, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error('Sipariş oluşturulurken bir hata oluştu');
      }

      const result = await response.json();
      showModal('Başarılı', 'Sipariş başarıyla oluşturuldu', 'success');
      
      // Başarılı sipariş oluşturma sonrası yeni sipariş numarası oluştur
      await generateOrderNumber();
      
      setTimeout(() => {
        navigate('/orders');
      }, 2000);
    } catch (error) {
      console.error('Sipariş oluşturma hatası:', error);
      showModal('Hata', 'Sipariş oluşturulurken bir hata oluştu', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Yeni Sipariş Oluştur</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Temel Bilgiler */}
        <div className="bg-white rounded-2xl shadow-md p-8 transition-all duration-300 hover:shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Temel Bilgiler</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Üretici Seçin
              </label>
              <VendorSelector
                selectedVendor={selectedVendor}
                onVendorSelect={handleVendorSelect}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sipariş Tarihi
              </label>
              <input
                type="datetime-local"
                name="orderDate"
                value={formData.orderDate}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Termin Tarihi
              </label>
              <input
                type="date" 
                name="terminDate"
                value={formData.terminDate}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ödeme Tipi
              </label>
              <select
                name="paymentType"
                value={formData.paymentType}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="Peşin">Peşin</option>
                <option value="Vadeli">Vadeli</option>
              </select>
            </div>
          </div>
          
          {selectedVendor && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="font-medium text-gray-700 mb-2">Seçilen Üretici Bilgileri</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Üretici ID:</span> {selectedVendor.producerId}
                </div>
                <div>
                  <span className="font-medium">Firma Adı:</span> {selectedVendor.companyName}
                </div>
                <div>
                  <span className="font-medium">Yetkili:</span> {selectedVendor.name}
                </div>
                <div>
                  <span className="font-medium">E-posta:</span> {selectedVendor.email}
                </div>
                <div>
                  <span className="font-medium">Telefon:</span> {selectedVendor.phone}
                </div>
                <div className="col-span-2">
                  <span className="font-medium">Adres:</span> {selectedVendor.address}
                </div>
                {selectedVendor.note && (
                  <div className="col-span-2">
                    <span className="font-medium">Not:</span> {selectedVendor.note}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Sipariş Numarası</label>
              <input
                type="text"
                name="orderNumber"
                value={formData.orderNumber}
                readOnly
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
          </div>
        </div>

        {/* Ürün Detayları */}
        <div className="bg-white rounded-2xl shadow-md p-8 transition-all duration-300 hover:shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-green-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Ürün Detayları</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ürün Seçin
              </label>
              <ProductSelector
                selectedProduct={selectedProduct}
                onProductSelect={handleProductSelect}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Sezon</label>
              <input
                type="text"
                name="season"
                value={formData.season}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              />
            </div>

            <div className="md:col-span-2 space-y-2">
              <label className="block text-sm font-medium text-gray-700">Notlar</label>
              <textarea
                name="productDescription"
                value={formData.productDescription}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                rows={3}
                placeholder="Ürün ile ilgili ek notlar..."
              />
            </div>

            {selectedProduct && (
              <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">Seçilen Ürün Bilgileri</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Ürün Kodu:</span> {selectedProduct.productCode}
                  </div>
                  <div>
                    <span className="font-medium">Model Adı:</span> {selectedProduct.modelName}
                  </div>
                  <div>
                    <span className="font-medium">Kategori:</span> {selectedProduct.category}
                  </div>
                  <div>
                    <span className="font-medium">Alt Kategori:</span> {selectedProduct.subCategory}
                  </div>
                  <div className="col-span-2">
                    <span className="font-medium">Açıklama:</span> {selectedProduct.description}
                  </div>
                  {selectedProduct.imageUrl && (
                    <div className="col-span-2">
                      <img src={selectedProduct.imageUrl} alt={selectedProduct.modelName} className="w-32 h-32 object-cover rounded-lg" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fiyatlandırma */}
        <div className="bg-white rounded-2xl shadow-md p-8 transition-all duration-300 hover:shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-amber-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Fiyatlandırma</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Optimum Değer (₺)</label>
              <input
                type="number"
                name="optimumValue"
                value={formData.optimumValue}
                onChange={handleInputChange}
                placeholder="Birim fiyat giriniz"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Sipariş Adeti</label>
              <input
                type="number"
                name="orderCount"
                value={formData.orderCount}
                onChange={handleInputChange}
                placeholder="Adet giriniz"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Toplam Tutar</label>
              <input
                type="text"
                value={`${formData.totalAmount.toLocaleString('tr-TR')} ₺`}
                readOnly
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 transition-all duration-300"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">KDV Tutarı (%10)</label>
              <input
                type="text"
                value={`${formData.kdvTotal.toLocaleString('tr-TR')} ₺`}
                readOnly
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50 transition-all duration-300"
              />
            </div>
          </div>
        </div>

        {/* Asortik Sipariş */}
        <div className="bg-white rounded-2xl shadow-md p-8 transition-all duration-300 hover:shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Asorti Sipariş</h3>
            </div>
            <div className="bg-indigo-50 px-4 py-2 rounded-lg">
              <div className="text-sm font-medium text-indigo-600">
                Toplam Miktar: {formData.totalOrderQuantity}
              </div>
            </div>
          </div>
          <AssortiTable
            columns={formData.assortiColumns}
            values={formData.assortiValues}
            onColumnsChange={handleAssortiColumnsChange}
            onValuesChange={handleAssortiValuesChange}
          />
        </div>

        {/* Form Butonları */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-300"
          >
            İptal
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300"
          >
            Siparişi Oluştur
          </button>
        </div>
      </form>
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </div>
  );
};

export default CreateOrder;